import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { FileDown } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { AssessmentCampaign, Employee, Nom35Assessment } from '@/types';
import { computeGlobalExecutiveNom35 } from '@/lib/nom35Executive';
import { ExecutivePdfDownloadButton } from '@/components/pdf/Nom35ExecutiveReportPdf';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, getRiskLevelVariant } from '@/components/ui/Badge';
import { generateNom35CampaignHistoricalPdf } from '@/lib/nom35PdfReport';

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

function formatDateShort(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
}

function normalizeLevel(level?: string): 'nulo' | 'bajo' | 'medio' | 'alto' | 'muyalto' | 'unknown' {
  const x = (level ?? '').toLowerCase().replace(/\s/g, '');
  if (x === 'nulo') return 'nulo';
  if (x === 'bajo') return 'bajo';
  if (x === 'medio') return 'medio';
  if (x === 'alto') return 'alto';
  if (x === 'muyalto') return 'muyalto';
  return 'unknown';
}

export function Nom035ResultsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { appUser, canAccessCompany } = useAuth();

  const [state, setState] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<AssessmentCampaign | null>(null);
  const [completedAssessments, setCompletedAssessments] = useState<Nom35Assessment[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | undefined>(undefined);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!campaignId) {
        setError('Falta campaignId');
        setState('error');
        return;
      }
      setState('loading');
      setError(null);
      setCampaign(null);
      setCompletedAssessments([]);
      setEmployees([]);
      setCompanyName('');
      setCompanyLogoUrl(undefined);

      try {
        const campaignSnap = await getDoc(doc(db, 'assessment_campaigns', campaignId));
        if (!campaignSnap.exists()) {
          throw new Error('Campaña no encontrada.');
        }
        const c = campaignSnap.data();
        const createdAtRaw = c.createdAt;
        let createdAt = new Date().toISOString();
        if (createdAtRaw) {
          const ts = createdAtRaw as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') createdAt = ts.toDate().toISOString();
          else if (typeof createdAtRaw === 'string') createdAt = createdAtRaw;
        }
        const closedAtRaw = c.closedAt;
        let closedAt: string | undefined;
        if (closedAtRaw) {
          const ts = closedAtRaw as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') closedAt = ts.toDate().toISOString();
          else if (typeof closedAtRaw === 'string') closedAt = closedAtRaw;
        }

        const campaignParsed: AssessmentCampaign = {
          id: campaignSnap.id,
          companyId: c.companyId as string,
          title: (c.title as string) ?? 'Campaña NOM-035',
          type: 'NOM35',
          status: (c.status as 'draft' | 'active' | 'closed') ?? 'closed',
          createdAt,
          closedAt,
        };

        if (!canAccessCompany(campaignParsed.companyId)) {
          throw new Error('Acceso restringido: no tienes permisos para ver esta campaña.');
        }

        const assessmentsSnap = await getDocs(
          query(
            collection(db, 'nom35_assessments'),
            where('companyId', '==', campaignParsed.companyId),
            where('campaignId', '==', campaignParsed.id),
            where('status', '==', 'completed')
          )
        );

        const list: Nom35Assessment[] = assessmentsSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            companyId: data.companyId as string,
            campaignId: (data.campaignId as string) ?? campaignParsed.id,
            employeeId: data.employeeId as string,
            employeeName: data.employeeName as string,
            guideType: data.guideType as Nom35Assessment['guideType'],
            status: 'completed',
            createdAt: (data.createdAt as string) ?? createdAt,
            completedAt: data.completedAt as string | undefined,
            answers: data.answers as Record<string, number> | undefined,
            resultScore: data.resultScore as number | undefined,
            resultLevel: data.resultLevel as string | undefined,
            requiresClinicalAttention: data.requiresClinicalAttention as boolean | undefined,
            nextAssessmentId: data.nextAssessmentId as string | undefined,
          } as Nom35Assessment;
        });

        list.sort((a, b) => {
          const da = a.completedAt || a.createdAt || '';
          const dbb = b.completedAt || b.createdAt || '';
          return dbb.localeCompare(da);
        });

        const [companySnap, employeesSnap] = await Promise.all([
          getDoc(doc(db, 'companies', campaignParsed.companyId)),
          getDocs(
            query(collection(db, 'employees'), where('companyId', '==', campaignParsed.companyId))
          ),
        ]);

        let resolvedCompanyName = 'Empresa';
        let resolvedLogo: string | undefined;
        if (companySnap.exists()) {
          const cd = companySnap.data();
          resolvedCompanyName = (cd.name as string)?.trim() || resolvedCompanyName;
          resolvedLogo = (cd.branding as { logoUrl?: string } | undefined)?.logoUrl;
        }

        const employeesList: Employee[] = employeesSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            companyId: data.companyId as string,
            firstName: data.firstName as string,
            lastName: data.lastName as string,
            email: data.email as string,
            phone: data.phone as string | undefined,
            position: data.position as string | undefined,
            department: data.department as string | undefined,
            dateOfHire: (data.dateOfHire as string) ?? '',
            hireDate: data.hireDate as string | undefined,
            isActive: data.isActive !== false,
            archivedAt: data.archivedAt as string | undefined,
            sex: data.sex as Employee['sex'] | undefined,
            birthDate: data.birthDate as string | undefined,
            maritalStatus: data.maritalStatus as string | undefined,
            educationLevel: data.educationLevel as string | undefined,
            jobType: data.jobType as string | undefined,
            contractType: data.contractType as string | undefined,
          } as Employee;
        });

        if (cancelled) return;
        setCampaign(campaignParsed);
        setCompletedAssessments(list);
        setEmployees(employeesList);
        setCompanyName(resolvedCompanyName);
        setCompanyLogoUrl(resolvedLogo);
        setState('loaded');
      } catch (err) {
        if (cancelled) return;
        console.error('[NOM-035][hist results] error:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar resultados.');
        setState('error');
      }
    };

    // Evita cargar mientras aún no tenemos el usuario listo.
    if (!appUser) return;
    run();
    return () => {
      cancelled = true;
    };
  }, [campaignId, appUser, canAccessCompany]);

  const stats = useMemo(() => {
    const total = completedAssessments.length;
    const byRisk = { nulo: 0, bajo: 0, medio: 0, alto: 0, muyalto: 0, unknown: 0 };
    let clinical = 0;

    for (const a of completedAssessments) {
      if (a.guideType === 'NOM35_G1') {
        if (a.requiresClinicalAttention === true) clinical++;
        continue;
      }
      const k = normalizeLevel(a.resultLevel);
      byRisk[k]++;
    }
    return { total, byRisk, clinical };
  }, [completedAssessments]);

  const executiveData = useMemo(
    () => computeGlobalExecutiveNom35(completedAssessments, employees),
    [completedAssessments, employees]
  );

  const reportGeneratedAt = useMemo(
    () =>
      new Date().toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    [campaign?.id]
  );

  const showExecutivePdf = Boolean(
    state === 'loaded' && campaign && completedAssessments.length > 0
  );

  const handleDownloadPdf = async () => {
    if (!campaign) return;
    setPdfLoading(true);
    try {
      const companySnap = await getDoc(doc(db, 'companies', campaign.companyId));
      const logoUrl = companySnap.exists()
        ? (companySnap.data()?.branding as { logoUrl?: string } | undefined)?.logoUrl
        : null;
      await generateNom35CampaignHistoricalPdf(
        {
          title: campaign.title,
          createdAt: campaign.createdAt,
          closedAt: campaign.closedAt,
          status: campaign.status,
        },
        {
          totalCompleted: stats.total,
          highRiskG2G3: stats.byRisk.alto + stats.byRisk.muyalto,
          clinicalG1: stats.clinical,
        },
        completedAssessments,
        logoUrl
      );
    } catch (err) {
      console.error('[NOM-035][hist results] PDF:', err);
      window.alert(err instanceof Error ? err.message : 'Error al generar el PDF. Permite ventanas emergentes.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Resultados NOM-035</h1>
          <p className="text-muted-foreground">
            Vista de solo lectura de una campaña histórica.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={state !== 'loaded' || !campaign || pdfLoading}
            onClick={() => void handleDownloadPdf()}
          >
            <FileDown className="mr-2 h-4 w-4" />
            {pdfLoading ? 'Generando…' : 'Descargar PDF'}
          </Button>
          {showExecutivePdf && campaign ? (
            <Button variant="outline" asChild>
              <ExecutivePdfDownloadButton
                data={executiveData}
                companyName={companyName || 'Empresa'}
                centerName={campaign.title}
                date={reportGeneratedAt}
                clientLogoUrl={companyLogoUrl}
              />
            </Button>
          ) : null}
          <Button variant="ghost" asChild>
            <Link to="/app/nom035">← Volver a NOM-035</Link>
          </Button>
        </div>
      </div>

      {state === 'loading' && (
        <div className="rounded-lg border bg-muted/50 p-4 text-sm text-muted-foreground">
          Cargando resultados...
        </div>
      )}

      {state === 'error' && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error ?? 'Error al cargar resultados'}
        </div>
      )}

      {state === 'loaded' && campaign && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{campaign.title}</CardTitle>
              <CardDescription>
                Inicio: {formatDateShort(campaign.createdAt)} · Cierre: {formatDateShort(campaign.closedAt)} · Estado:{' '}
                <span className="font-medium">{campaign.status}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg border bg-card p-4">
                  <p className="text-2xl font-semibold tabular-nums">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Evaluaciones completadas</p>
                </div>
                <div className="rounded-lg border bg-card p-4">
                  <p className="text-2xl font-semibold tabular-nums">{stats.byRisk.alto + stats.byRisk.muyalto}</p>
                  <p className="text-sm text-muted-foreground">Riesgo Alto/Muy Alto (G2/G3)</p>
                </div>
                <div className="rounded-lg border bg-card p-4">
                  <p className="text-2xl font-semibold tabular-nums">{stats.clinical}</p>
                  <p className="text-sm text-muted-foreground">Atención clínica (Guía I)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resultados individuales</CardTitle>
              <CardDescription>
                Se muestran evaluaciones con `status: completed` para la campaña seleccionada.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {completedAssessments.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No hay evaluaciones completadas en esta campaña.</p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-left font-medium">Empleado</th>
                        <th className="px-4 py-2.5 text-left font-medium">Fecha</th>
                        <th className="px-4 py-2.5 text-left font-medium">Guía</th>
                        <th className="px-4 py-2.5 text-left font-medium">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedAssessments.map((a) => (
                        <tr key={a.id} className="border-b last:border-0">
                          <td className="px-4 py-2.5 font-medium">{a.employeeName}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{formatDateShort(a.completedAt ?? a.createdAt)}</td>
                          <td className="px-4 py-2.5">
                            {a.guideType === 'NOM35_G1' && 'Guía I (ATS)'}
                            {a.guideType === 'NOM35_G2' && 'Guía II'}
                            {a.guideType === 'NOM35_G3' && 'Guía III'}
                          </td>
                          <td className="px-4 py-2.5">
                            {a.guideType === 'NOM35_G1' ? (
                              a.requiresClinicalAttention === true ? (
                                <Badge variant="muyAlto">Requiere Atención</Badge>
                              ) : (
                                <Badge variant="bajo">Sin Riesgo</Badge>
                              )
                            ) : (
                              <Badge variant={getRiskLevelVariant(a.resultLevel)}>
                                {a.resultLevel ?? '—'}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

